// Kunde anlegen / bearbeiten — ein Screen, am Handy mit einer Hand bedienbar.
//
// Reihenfolge nach echter Nutzung, nicht nach Datenbankspalten:
//   Adresse (das Erste, was der Bauer weiß) → Name (OPTIONAL! 58 von 223 Kunden
//   haben keinen) → Lieferhinweis → Korb → speichern.

import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useCustomerFull, useSaveCustomer, type BasketItem } from "@/lib/crm";
import type { CustomerType } from "@/lib/db";
import { useProducts, INTEREST_LABELS, type InterestTag } from "@/lib/db";
import { useKundenRechnungen, zahlungsstand, istUeberfaellig } from "@/lib/buchhaltung";
import { BasketSheet, type BasketDraft } from "./BasketSheet";
import { TurnusCard } from "./TurnusSheet";
import { Button, Card, Field, Input, Segmented, Spinner, cn, euro, qtyFmt } from "@/design-system/ui";
import { ModulHeader } from "@/modules/home/ModulHeader";

const DIENSTAG = 2;

export default function CustomerEditPage() {
  const { id } = useParams<{ id: string }>();
  const isNew = !id || id === "neu";
  const navigate = useNavigate();

  const { data: existing, isLoading } = useCustomerFull(isNew ? undefined : id);
  const { data: products = [] } = useProducts({ onlyActive: true });
  const save = useSaveCustomer();

  const [name, setName] = useState("");
  const [street, setStreet] = useState("");
  // Voreingestellt, weil bisher jeder Kunde in Gütersloh liegt — aber änderbar:
  // sobald Touren in andere Orte gehen, braucht es das Feld.
  const [plz, setPlz] = useState("33330");
  const [ort, setOrt] = useState("Gütersloh");
  const [notes, setNotes] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [wishTime, setWishTime] = useState("");
  const [typ, setTyp] = useState<CustomerType>("privat");
  const [basketOpen, setBasketOpen] = useState(false);
  /** Was ist beim Speichern schiefgegangen? NULL = nichts. */
  const [fehler, setFehler] = useState<string | null>(null);

  const [basket, setBasket] = useState<BasketDraft>({
    items: [],
    weekdays: [DIENSTAG],
    intervalWeeks: 1,
    interestTags: [],
    preferredTime: null,
  });

  // Bestandskunde EINMAL in den Formularzustand laden.
  // Ohne diese Sperre würde jeder Refetch (Fensterwechsel, Reconnect) die gerade
  // getippten Eingaben mit den alten DB-Werten überschreiben.
  const loadedFor = useRef<string | null>(null);

  useEffect(() => {
    if (!existing) return;
    if (loadedFor.current === existing.id) return;
    loadedFor.current = existing.id;

    const street0 = existing.addresses?.[0]?.street ?? "";
    // 58 der 223 Kunden haben keinen Namen — bei ihnen steht die Adresse als
    // display_name. Dann muss das Namensfeld LEER bleiben, sonst sieht es aus,
    // als hieße der Kunde "Mozartweg 6".
    setName(existing.display_name === street0 ? "" : existing.display_name);
    setStreet(street0);
    setPlz(existing.addresses?.[0]?.zip ?? "33330");
    setOrt(existing.addresses?.[0]?.city ?? "Gütersloh");
    setNotes(existing.notes ?? "");
    setPhone(existing.phone ?? "");
    setEmail(existing.email ?? "");
    setWishTime(existing.preferred_time?.slice(0, 5) ?? "");
    setTyp(existing.type ?? "privat");

    const sub = existing.subscriptions?.[0];
    setBasket({
      items: sub?.items.map((i) => ({ product_id: i.product_id, qty: Number(i.qty) })) ?? [],
      weekdays: sub?.weekdays ?? [DIENSTAG],
      intervalWeeks: sub?.interval_weeks ?? 1,
      interestTags: existing.interest_tags ?? [],
      preferredTime: existing.preferred_time?.slice(0, 5) ?? null,
    });
  }, [existing]);

  const basketSummary = useMemo(
    () => summarize(basket.items, products),
    [basket.items, products]
  );

  const canSave = street.trim().length > 2 && ort.trim().length > 1;

  /**
   * Scheitert das Speichern, MUSS es sichtbar sein.
   *
   * Hier stand ein blankes `await save.mutateAsync(...)`. Wirft die Mutation,
   * fängt React Query den Fehler ab, der Spinner hört auf — und sonst passiert
   * nichts. Genau so hat Fabian es erlebt: Formular ausgefüllt, "Kunde anlegen"
   * getippt, nichts. Der Grund lag in der Datenbank (ein Fahrer durfte nicht
   * schreiben), aber ohne Meldung war das von aussen nicht zu erkennen.
   */
  const onSubmit = async () => {
    setFehler(null);
    try {
      const gespeichert = await save.mutateAsync({
        id: isNew ? undefined : id,
        display_name: name.trim(),
        street: street.trim(),
        city: ort.trim(),
        zip: plz.trim(),
        notes: notes.trim() || null,
        phone: phone.trim() || null,
        email: email.trim() || null,
        interest_tags: basket.interestTags,
        preferred_time: wishTime || null,
        type: typ,
        items: basket.items,
        weekdays: basket.weekdays,
        interval_weeks: basket.intervalWeeks,
      });

      // Adresse nicht auffindbar? Dann NICHT weiternavigieren. Der Kunde ist
      // gespeichert, aber ohne Koordinaten fällt er aus jeder Route — und das
      // merkt sonst niemand, bis er am Dienstag fehlt. Hier stehenbleiben heisst:
      // Die Straße lässt sich sofort korrigieren und erneut speichern.
      if (gespeichert.geoFehler) {
        setFehler(gespeichert.geoFehler);
        return;
      }
      // Konnte der Kunde nicht sofort auf die Touren gesetzt werden, ist das
      // kein Grund, den gespeicherten Kunden infrage zu stellen — aber es muss
      // dastehen. Sonst sucht man ihn am Liefertag vergeblich auf der Tour.
      if (gespeichert.planFehler) {
        setFehler(gespeichert.planFehler);
        return;
      }
      navigate("/crm/kunden");
    } catch (e) {
      const m = (e as Error).message ?? "";
      setFehler(
        /failed to fetch|load failed|networkerror/i.test(m)
          ? "Kein Netz — der Kunde wurde NICHT gespeichert. Bitte noch einmal tippen."
          : /row-level security|permission|denied|policy/i.test(m)
            ? "Dafür fehlt dir die Berechtigung. Bitte im Büro melden."
            : m || "Der Kunde konnte nicht gespeichert werden."
      );
    }
  };

  if (!isNew && isLoading) {
    return (
      <div className="flex justify-center py-16 text-ink-400">
        <Spinner />
      </div>
    );
  }

  return (
    <div className="mx-auto flex h-full max-w-xl flex-col">
      {/* Dieselbe Kopfzeile wie auf jeder anderen Seite — vorher baute sich diese
          Seite ihre eigene: andere Höhe, anderer Zurück-Pfeil, und ohne das
          Profil oben rechts. Beim Wechsel sprang die Leiste sichtbar. */}
      <ModulHeader
        titel={isNew ? "Neuer Kunde" : (existing?.display_name ?? "Kunde")}
        untertitel={existing?.customer_number ?? undefined}
        zurueck={() => navigate("/crm/kunden")}
      />

      <div className="min-h-0 flex-1 space-y-5 overflow-y-auto px-4 py-5">
        <Field label="Straße + Hausnummer">
          <Input
            value={street}
            onChange={(e) => setStreet(e.target.value)}
            placeholder="z.B. Wichernstraße 24"
            autoFocus={isNew}
            autoComplete="off"
          />
        </Field>

        {/* PLZ und Ort in EINER Zeile — so schreibt man eine Adresse, und so
            liest man sie auch. Gütersloh ist voreingestellt (dort liegen alle
            bisherigen Kunden), lässt sich aber überschreiben, sobald eine Tour
            woanders hinfährt. Die feste Spaltenbreite für die PLZ, weil ein
            `w-24` von außen gegen die Basisklasse `w-full` des Inputs verliert —
            `cn()` ist ein reiner String-Join, kein tailwind-merge. */}
        <div className="flex gap-3">
          <div className="w-28 shrink-0">
            <Field label="PLZ">
              <Input
                value={plz}
                onChange={(e) => setPlz(e.target.value)}
                placeholder="33330"
                inputMode="numeric"
                autoComplete="off"
              />
            </Field>
          </div>
          <div className="min-w-0 flex-1">
            <Field label="Ort">
              <Input
                value={ort}
                onChange={(e) => setOrt(e.target.value)}
                placeholder="Gütersloh"
                autoComplete="off"
              />
            </Field>
          </div>
        </div>

        <Field label="Name" optional hint="Viele Kunden laufen nur über die Adresse">
          <Input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="z.B. Körkemeyer"
            autoComplete="off"
          />
        </Field>

        <Field
          label="Art"
          hint={
            typ === "gewerblich"
              ? "Bekommt zu jeder Lieferung eine Rechnung als PDF — auch bei Barzahlung."
              : "Bekommt eine Rechnung nur auf Nachfrage."
          }
        >
          <Segmented
            value={typ}
            onChange={setTyp}
            options={[
              { value: "privat", label: "Privat" },
              { value: "gewerblich", label: "Gewerblich" },
            ]}
          />
        </Field>

        <Field label="Lieferhinweis" optional hint="„kommt ans Auto“, „stellt raus“, „hinter den Blumenkübel“">
          <Input
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Wie wird geliefert?"
          />
        </Field>

        <Field label="Wunsch-Lieferzeit" optional hint="Wenn gesetzt, plant die Tour um diesen Kunden herum (±15 min)">
          <Input
            type="time"
            value={wishTime}
            onChange={(e) => setWishTime(e.target.value)}
            className="tabular-nums"
          />
        </Field>

        {/* ---- Korb ---- */}
        <Card className="p-4">
          <div className="mb-3 flex items-center justify-between">
            <h2 className="text-xs font-medium uppercase tracking-wider text-ink-400">
              Regelmäßiger Korb
            </h2>
            <Button size="sm" onClick={() => setBasketOpen(true)}>
              {basket.items.length ? "Ändern" : "Packen"}
            </Button>
          </div>

          {basket.items.length === 0 ? (
            <p className="text-sm text-ink-500">Noch kein Korb — der Kunde bekommt keine Lieferung.</p>
          ) : (
            <>
              <ul className="space-y-1.5 text-[15px]">
                {basketSummary.lines.map((l) => (
                  <li key={l.label} className="flex items-baseline justify-between gap-3">
                    <span className="min-w-0 truncate text-ink-200">{l.label}</span>
                    <span className="shrink-0 tabular-nums text-ink-400">{euro(l.cents)}</span>
                  </li>
                ))}
              </ul>
              <div className="mt-3 flex items-baseline justify-between border-t border-ink-700/70 pt-3">
                <span className="text-sm text-ink-400">
                  {rhythmLabel(basket.weekdays, basket.intervalWeeks)}
                </span>
                <span className="text-[15px] font-semibold tabular-nums text-ink-50">
                  {euro(basketSummary.total)}
                </span>
              </div>
            </>
          )}

          {basket.interestTags.length > 0 && (
            <div className="mt-3 flex flex-wrap gap-1.5 border-t border-ink-700/70 pt-3">
              <span className="text-sm text-ink-500">Nimmt gern mit:</span>
              {basket.interestTags.map((t: InterestTag) => (
                <span key={t} className="text-sm text-gruen-400">
                  {INTEREST_LABELS[t]}
                </span>
              ))}
            </div>
          )}
        </Card>

        {/* Turnus — Rhythmus + Startpunkt, vom Fahrer frei setzbar. Zeigt sich
            nur, wenn der Kunde ein aktives Abo mit Liefertag hat. */}
        {!isNew && id && <TurnusCard customerId={id} customerName={name.trim() || street.trim()} />}

        {/* Belege — was hat der Kunde bekommen, was hat er bezahlt */}
        {!isNew && id && <Belege customerId={id} />}

        {/* Kontaktdaten: heute leer, aber für den späteren Mail-Versand gebraucht */}
        <details className="group">
          <summary className="cursor-pointer list-none text-sm text-ink-400 transition-colors duration-fast hover:text-ink-200">
            <span className="group-open:hidden">＋ Telefon / E-Mail</span>
            <span className="hidden group-open:inline">− Telefon / E-Mail</span>
          </summary>
          <div className="mt-3 space-y-4">
            <Field label="Telefon" optional>
              <Input value={phone} onChange={(e) => setPhone(e.target.value)} type="tel" />
            </Field>
            <Field label="E-Mail" optional hint="Später für Rechnungsversand statt Brief">
              <Input value={email} onChange={(e) => setEmail(e.target.value)} type="email" />
            </Field>
          </div>
        </details>
      </div>

      {/* shrink-0: Ohne das darf der Footer in einem Flex-Container schrumpfen —
          bei langem Inhalt wird der Speichern-Knopf dann flacher, bis er kaum
          noch zu treffen ist. */}
      <footer className="shrink-0 border-t border-ink-700/70 bg-ink-900/95 px-4 py-4 pb-[max(1rem,env(safe-area-inset-bottom))]">
        {fehler && (
          <p className="mb-3 rounded-lg border border-danger-bg/40 bg-danger-bg/10 px-3 py-2.5 text-sm text-danger-fg">
            {fehler}
          </p>
        )}
        <Button
          variant="primary"
          size="lg"
          className="w-full"
          disabled={!canSave}
          loading={save.isPending}
          onClick={onSubmit}
        >
          {isNew ? "Kunde anlegen" : "Speichern"}
        </Button>
        {!canSave && (
          <p className="mt-2 text-center text-sm text-ink-500">
            {street.trim().length > 2 ? "Der Ort fehlt noch." : "Straße und Hausnummer fehlen noch."}
          </p>
        )}
      </footer>

      {/* Erst beim Öffnen mounten: der Sheet leitet seinen Anfangszustand aus
          items+products ab, und useState würde ihn nicht nachziehen, wenn die
          Produkte erst nach dem ersten Render eintreffen — der Korb erschiene leer. */}
      {basketOpen && (
        <BasketSheet
          open
          onClose={() => setBasketOpen(false)}
          initial={basket}
          saving={false}
          onSave={(d) => {
            setBasket(d);
            setBasketOpen(false);
          }}
        />
      )}
    </div>
  );
}

/**
 * Belege des Kunden. Jede Lieferung erzeugt einen Lieferschein und eine Rechnung —
 * hier stehen sie, neueste zuerst, mit ihrem Zahlungsstand. So sieht auch das
 * Büro auf einen Blick, wer noch schuldet.
 */
function Belege({ customerId }: { customerId: string }) {
  const { data: rechnungen = [], isLoading } = useKundenRechnungen(customerId);

  if (isLoading) return null;

  const stand = zahlungsstand(rechnungen);
  const offen = stand.offeneAnzahl > 0;

  return (
    <Card className="p-4">
      <div className="mb-3 flex items-center justify-between">
        <h2 className="text-xs font-medium uppercase tracking-wider text-ink-400">Belege</h2>
        {rechnungen.length > 0 && (
          <span
            className={cn(
              "rounded px-2 py-0.5 text-xs font-medium",
              offen ? "bg-danger-bg/15 text-danger-fg" : "bg-gruen-500/15 text-gruen-400"
            )}
          >
            {offen ? `${euro(stand.offenCents)} offen` : "alles bezahlt"}
          </span>
        )}
      </div>

      {rechnungen.length === 0 ? (
        <p className="text-sm text-ink-500">
          Noch keine Lieferung — Belege entstehen automatisch bei der Übergabe.
        </p>
      ) : (
        <ul className="space-y-2">
          {rechnungen.slice(0, 8).map((r) => {
            const bezahlt = r.status === "paid";
            const spaet = istUeberfaellig(r);
            return (
              <li
                key={r.id}
                className="flex items-center justify-between gap-3 border-b border-ink-700/50 pb-2 last:border-0 last:pb-0"
              >
                <div className="min-w-0">
                  <div className="text-[15px] tabular-nums text-ink-200">
                    {new Date(r.period_start).toLocaleDateString("de-DE", {
                      day: "2-digit",
                      month: "2-digit",
                      year: "2-digit",
                    })}
                  </div>
                  <div className="mt-0.5 font-mono text-xs text-ink-500">{r.invoice_number}</div>
                </div>

                <div className="shrink-0 text-right">
                  <div className="text-[15px] font-semibold tabular-nums text-ink-50">
                    {euro(r.total_cents)}
                  </div>
                  <div
                    className={cn(
                      "mt-0.5 text-xs font-medium",
                      bezahlt
                        ? "text-gruen-400"
                        : spaet
                          ? "text-danger-fg"
                          : "text-ink-400"
                    )}
                  >
                    {bezahlt ? "bezahlt" : spaet ? "überfällig" : "offen"}
                  </div>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </Card>
  );
}

function summarize(items: BasketItem[], products: { id: string; name: string; unit: string; price_cents: number }[]) {
  const lines: { label: string; cents: number }[] = [];
  let total = 0;
  for (const it of items) {
    const p = products.find((x) => x.id === it.product_id);
    if (!p) continue;
    const cents = Math.round(it.qty * p.price_cents);
    total += cents;
    lines.push({
      label: p.unit === "kg" ? `${qtyFmt(it.qty)} kg ${p.name}` : `${qtyFmt(it.qty)}× ${p.name}`,
      cents,
    });
  }
  return { lines, total };
}

function rhythmLabel(weekdays: number[], interval: number) {
  const names = ["Mo", "Di", "Mi", "Do", "Fr", "Sa"];
  const days = weekdays.map((d) => names[d - 1]).join(", ");
  const rhythm = interval === 1 ? "jede Woche" : `alle ${interval} Wochen`;
  return `${days} · ${rhythm}`;
}
